import { useEffect } from 'react';
import { useAuth } from '../../../public/site/AuthProvider';
import { useOnlineModelsContext } from '../../../public/site/onlineModel/onlineModelContextProvider';
import {
  expandRelatedModelCodes,
  filterByOnlineModelCodes,
  mergeMobileBroadbandModels,
} from '../../../public/site/onlineModel/onlineModelService';
import type { ShoppingBasketType } from '../../../common/types/shoppingBasket';
import type { OnlineModel } from '../../../generated/api/onlineModel';

/**
 * Fetches models for basket items. Expands MBB/ElisaNetti GUIDs and merges their offers.
 */
export const useModelsForBasket = (basket: ShoppingBasketType): OnlineModel[] => {
  const { isAuthenticated, isLoading, authenticatedUser } = useAuth();
  const { priceAlteredModels, fetchModelsByCodes, areModelCodesResolved } = useOnlineModelsContext();

  const basketGuids = [...new Set(basket.items?.map(item => item.guid) ?? [])];
  const expandedGuids = expandRelatedModelCodes(basketGuids);
  const expandedGuidsKey = expandedGuids.join(',');
  const authResolved = !isLoading;
  const privateModelsReady = authResolved && (!isAuthenticated || Boolean(authenticatedUser?.activeAccountMasterId));

  useEffect(() => {
    const guids = expandedGuidsKey ? expandedGuidsKey.split(',') : [];
    if (privateModelsReady && guids.length > 0) {
      fetchModelsByCodes(guids);
    }
  }, [expandedGuidsKey, fetchModelsByCodes, privateModelsReady]);

  if (!privateModelsReady) {
    return [];
  }

  const modelsByCode = priceAlteredModels.onlineModelsByCode;

  const models = filterByOnlineModelCodes(modelsByCode, expandedGuids);

  // When basket triggered expansion (e.g. SP WiFi → also need MBB + ElisaNetti),
  // return empty until all expanded codes are API-resolved. Resolved means backend answered
  // for the code (found or not-found), which prevents EOL products from blocking rendering.
  const hasExpandedCodes = expandedGuids.length > basketGuids.length;
  if (hasExpandedCodes && !areModelCodesResolved(expandedGuids)) {
    return [];
  }
  return mergeMobileBroadbandModels(models);
};
